import { FC, useMemo, useState } from 'react';
import ReactSelect, { components, createFilter, MultiValue, OptionProps, StylesConfig } from 'react-select';
import {
  useGetCommunityAnalyticsAuthorsQuery,
  useGetCommunityAnalyticsOrganizationsQuery,
  useGetCommunityAnalyticsQuery,
  useLazyGetCommunityAnalyticsReportQuery,
} from '../api/api';

type FilterOption = {
  value: string;
  label: string;
  email?: string;
};

const authorFilter = createFilter<FilterOption>({
  stringify: ({ data }) => `${data.label} ${data.email ?? ''}`,
});

const CheckboxOption = (props: OptionProps<FilterOption, true>) => (
  <components.Option {...props}>
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        checked={props.isSelected}
        readOnly
        className="pointer-events-none h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <span>{props.label}</span>
    </div>
  </components.Option>
);

const filterSelectStyles: StylesConfig<FilterOption, true> = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
    '&:hover': { borderColor: '#3b82f6' },
  }),
  menu: (base) => ({ ...base, zIndex: 20 }),
  multiValue: (base) => ({ ...base, backgroundColor: '#dbeafe' }),
  multiValueLabel: (base) => ({ ...base, color: '#1d4ed8' }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#1d4ed8',
    ':hover': { backgroundColor: '#bfdbfe', color: '#1e3a8a' },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#eff6ff' : 'white',
    color: '#111827',
    cursor: 'pointer',
  }),
};

const columns = [
  { label: 'Scenario', key: 'title' },
  { label: 'Organization', key: 'organization_name' },
  { label: 'Author', key: 'author_full_name' },
  { label: 'Participants', key: 'trainee_count' },
  { label: 'Sessions', key: 'session_count' },
  { label: 'Completion rate', key: 'completion_rate' },
  { label: 'Pass rate', key: 'pass_rate' },
  { label: 'Score', key: 'score' },
  { label: 'Saves', key: 'saves_count' },
] as const;

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
type CommunityAnalyticsSortKey = (typeof columns)[number]['key'];

const formatDateBoundary = (date: string, endOfDay: boolean): string =>
  new Date(`${date}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`).toISOString();

export const CommunityAnalytics: FC = () => {
  const [organizationIds, setOrganizationIds] = useState<string[]>([]);
  const [authorIds, setAuthorIds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [columnKey, setColumnKey] = useState<CommunityAnalyticsSortKey>('title');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const { data, isError, isFetching, isLoading } = useGetCommunityAnalyticsQuery({
    organization_ids: organizationIds,
    author_ids: authorIds,
    created_from: startDate ? formatDateBoundary(startDate, false) : undefined,
    created_to: endDate ? formatDateBoundary(endDate, true) : undefined,
    page,
    limit,
    column_key: columnKey,
    order,
  });
  const { data: organizations, isLoading: isLoadingOrganizations } =
    useGetCommunityAnalyticsOrganizationsQuery();
  const { data: authors, isLoading: isLoadingAuthors } = useGetCommunityAnalyticsAuthorsQuery();
  const [getReport, { isFetching: isExporting }] = useLazyGetCommunityAnalyticsReportQuery();
  const documents = data?.documents ?? [];

  const organizationOptions = useMemo<FilterOption[]>(
    () =>
      organizations?.map((organization) => ({
        value: organization.organization_id,
        label: organization.name,
      })) ?? [],
    [organizations],
  );
  const authorOptions = useMemo<FilterOption[]>(
    () =>
      authors?.map((author) => ({
        value: author.id,
        label: [author.first_name, author.last_name].filter(Boolean).join(' ') || author.email,
        email: author.email,
      })) ?? [],
    [authors],
  );
  const total = data?.total ?? 0;
  const currentPage = data?.page ?? page;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const fromItem = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const toItem = Math.min(currentPage * limit, total);

  const onOrganizationChange = (values: MultiValue<FilterOption>) => {
    setPage(1);
    setOrganizationIds(values.map((option) => option.value));
  };

  const onAuthorChange = (values: MultiValue<FilterOption>) => {
    setPage(1);
    setAuthorIds(values.map((option) => option.value));
  };

  const toggleSort = (nextColumnKey: CommunityAnalyticsSortKey) => {
    setPage(1);

    if (nextColumnKey === columnKey) {
      setOrder((currentOrder) => (currentOrder === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setColumnKey(nextColumnKey);
    setOrder('asc');
  };

  const downloadReport = async () => {
    try {
      setExportError(null);
      const report = await getReport({
        organization_ids: organizationIds,
        author_ids: authorIds,
        created_from: startDate ? formatDateBoundary(startDate, false) : undefined,
        created_to: endDate ? formatDateBoundary(endDate, true) : undefined,
        locale: navigator.language || 'en',
      }).unwrap();
      const url = URL.createObjectURL(report);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'community-analytics.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Failed to download community analytics report.');
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Community Analytics</h1>
          <p className="mt-1 text-sm text-gray-600">
            Analytics for scenarios published in the community.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void downloadReport()}
          disabled={isExporting}
          className="cursor-pointer rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
        >
          {isExporting ? 'Preparing XLSX...' : 'Download XLSX'}
        </button>
      </div>

      {exportError && <p className="mb-4 text-sm text-red-600">{exportError}</p>}

      <div className="mb-2 grid max-w-4xl gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Organizations</label>
          <ReactSelect<FilterOption, true>
            isMulti
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            placeholder="Select organizations..."
            noOptionsMessage={() => 'No organizations found'}
            value={organizationOptions.filter((option) => organizationIds.includes(option.value))}
            onChange={onOrganizationChange}
            options={organizationOptions}
            isLoading={isLoadingOrganizations}
            components={{ Option: CheckboxOption }}
            styles={filterSelectStyles}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Authors</label>
          <ReactSelect<FilterOption, true>
            isMulti
            isClearable
            isSearchable
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            placeholder="Select authors..."
            noOptionsMessage={() => 'No authors found'}
            value={authorOptions.filter((option) => authorIds.includes(option.value))}
            onChange={onAuthorChange}
            options={authorOptions}
            isLoading={isLoadingAuthors}
            components={{ Option: CheckboxOption }}
            filterOption={authorFilter}
            styles={filterSelectStyles}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="community-analytics-start-date">
            From
          </label>
          <input
            id="community-analytics-start-date"
            type="date"
            value={startDate}
            onChange={(event) => {
              setStartDate(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="community-analytics-end-date">
            To
          </label>
          <input
            id="community-analytics-end-date"
            type="date"
            value={endDate}
            onChange={(event) => {
              setEndDate(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase whitespace-nowrap"
                  >
                    <button
                      type="button"
                      onClick={() => toggleSort(column.key)}
                      className="flex cursor-pointer items-center gap-1 hover:text-gray-900"
                    >
                      {column.label}
                      <span aria-hidden="true">
                        {column.key === columnKey ? (order === 'asc' ? '↑' : '↓') : '↕'}
                      </span>
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-gray-500">
                    Loading analytics...
                  </td>
                </tr>
              )}
              {isError && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-red-600">
                    Failed to load community analytics.
                  </td>
                </tr>
              )}
              {!isLoading && !isError && documents.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="px-6 py-10 text-center text-sm text-gray-500">
                    No community scenarios found.
                  </td>
                </tr>
              )}
              {documents.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex min-w-64 items-center gap-3">
                      <img
                        src={item.avatar_url}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded object-cover bg-gray-100"
                      />
                      <span className="text-sm font-medium text-gray-900">{item.title}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">
                    {item.organization_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {item.author.first_name} {item.author.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{item.author.email}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{item.participant_count}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{item.session_count}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{item.completion_rate}%</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{item.success_rate}%</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{item.score}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 text-right">{item.saves_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <label htmlFor="community-analytics-page-size">Rows per page</label>
          <select
            id="community-analytics-page-size"
            value={limit}
            onChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(1);
            }}
            className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {PAGE_SIZE_OPTIONS.map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
          <span>{isFetching ? 'Updating...' : `Showing ${fromItem}-${toItem} of ${total}`}</span>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((currentPageValue) => Math.max(1, currentPageValue - 1))}
              disabled={currentPage <= 1 || isFetching}
              className="cursor-pointer rounded-md bg-gray-600 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((currentPageValue) => Math.min(totalPages, currentPageValue + 1))}
              disabled={currentPage >= totalPages || isFetching}
              className="cursor-pointer rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
